/* eslint-disable no-use-before-define */
import { Map, Set } from 'immutable';

import { PLAYLIST_PAGE_SIZE, ReducerKeys } from './constants';
import * as D from './deps';

export const INITIAL_STATE = Map({});

// T3 PLAYLIST ENTITY REDUCER
export default D.createReducer(INITIAL_STATE, {
  [D.Constants.PlaylistT3.ActionTypes.ON_FEEDBACK_UPDATE_COMPLETION]: onFeedbackCompletion,
  [D.Constants.Api.ApiActions.PLAYLISTS_GET_TRACKS_SUCCESS]: onGetTracksSuccess,
  [D.Constants.Api.ApiActions.PLAYLISTS_GET_TRACKS_ATTEMPT]: onGetTracksAttempt,
  [D.Constants.Api.ApiActions.PLAYLISTS_GET_TRACKS_FAIL]: onGetTracksFail,
  [D.Constants.Api.ApiActions.PLAYLISTS_EDIT_TRACKS_SUCCESS]: onEditTracksSuccess,
  [D.Constants.Api.ApiActions.PLAYLISTS_APPEND_ITEMS_SUCCESS]: onAppendItemsSuccess,
  [D.Constants.PlaylistT3.ActionTypes.DELETE_TRACK]: onDeleteTrack,
  [D.Constants.PlaylistT3.ActionTypes.CLEAR_PLAYLIST_TRACKLIST]: clearTracklist,
  [D.Constants.Api.ApiActions.PLAYLISTS_DELETE_TRACKS_SUCCESS]: onDeleteTracksSuccess,
  [D.Constants.Api.ApiActions.PLAYLISTS_DELETE_TRACKS_FAIL]: onDeleteTracksFail,
});

// CASE FUNCTIONS

function onFeedbackCompletion(state, action) {
  const { playlist } = action.data;
  let resultState = state;
  if (playlist) {
    // whenever a track is possibly added or deleted from the top of the playlist, invalidate all offsets
    const playlistId = playlist.get('pandoraId');
    const totalTracks = playlist.get('totalTracks');
    const invalidOffsets = D.Utils.RandomAccess.getOffsetsInRange(
      totalTracks,
      PLAYLIST_PAGE_SIZE,
      0,
      totalTracks - 1,
    );
    resultState = invalidateOffsets(state, playlistId, invalidOffsets);
  }
  return resultState;
}

function onGetTracksAttempt(state, action) {
  const pandoraId = action.request.content.request.pandoraId;
  if (pandoraId) {
    const offset = action.request.content.request.offset;
    const previous = state.getIn([pandoraId, ReducerKeys.GET_TRACKS_OFFSETS_FETCHING]) || Set();
    if (offset >= 0) {
      return state.setIn(
        [pandoraId, ReducerKeys.GET_TRACKS_OFFSETS_FETCHING],
        previous.add(offset),
      );
    }
  }
  return state;
}

function onGetTracksSuccess(state, action) {
  let pandoraId = action.response.result;
  if (pandoraId) {
    const previousFetched =
      state.getIn([pandoraId, ReducerKeys.GET_TRACKS_OFFSETS_FETCHED]) || Set();
    const previousFetching =
      state.getIn([pandoraId, ReducerKeys.GET_TRACKS_OFFSETS_FETCHING]) || Set();
    const offset = action.params.content.request.offset;
    if (offset >= 0) {
      return state
        .setIn([pandoraId, ReducerKeys.GET_TRACKS_OFFSETS_FETCHED], previousFetched.add(offset))
        .setIn(
          [pandoraId, ReducerKeys.GET_TRACKS_OFFSETS_FETCHING],
          previousFetching.remove(offset),
        );
    }
  } else {
    pandoraId = action.params.content.request.pandoraId;
    const offset = action.params.content.request.offset;
    if (pandoraId && offset >= 0) {
      const previousFetching =
        state.getIn([pandoraId, ReducerKeys.GET_TRACKS_OFFSETS_FETCHING]) || Set();
      return state.setIn(
        [pandoraId, ReducerKeys.GET_TRACKS_OFFSETS_FETCHING],
        previousFetching.remove(offset),
      );
    }
  }
  return state;
}

function onGetTracksFail(state, action) {
  try {
    const data = JSON.parse(action.error.config.data);
    const pandoraId = data.request.pandoraId;
    const offset = data.request.offset;
    if (pandoraId && offset) {
      const previousFetching =
        state.getIn([pandoraId, ReducerKeys.GET_TRACKS_OFFSETS_FETCHING]) || Set();
      return state.setIn(
        [pandoraId, ReducerKeys.GET_TRACKS_OFFSETS_FETCHING],
        previousFetching.remove(offset),
      );
    }
    return state;
  } catch (err) {
    return state.map(playlist => playlist.set(ReducerKeys.GET_TRACKS_OFFSETS_FETCHING, Set()));
  }
}

function clearTracklist(state, action) {
  const { pandoraId } = action.data;
  return state
    .setIn([pandoraId, ReducerKeys.GET_TRACKS_OFFSETS_FETCHED], Set())
    .setIn([pandoraId, ReducerKeys.GET_TRACKS_OFFSETS_FETCHING], Set());
}

function invalidateOffsets(state, pandoraId, invalidOffsets) {
  let newFetchedOffsets = state.getIn([pandoraId, ReducerKeys.GET_TRACKS_OFFSETS_FETCHED]) || Set();
  invalidOffsets.forEach(offset => {
    if (offset >= 0 && newFetchedOffsets.has(offset)) {
      newFetchedOffsets = newFetchedOffsets.remove(offset);
    }
  });
  return state.setIn([pandoraId, ReducerKeys.GET_TRACKS_OFFSETS_FETCHED], newFetchedOffsets);
}

function onEditTracksSuccess(state, action) {
  const { pandoraId, totalTracks } = action.response;
  const { moves, inserts } = action.params.content.request;
  if (pandoraId && totalTracks >= 0) {
    let startIndex = 0;
    let endIndex = 0;
    if (moves) {
      const move = moves.length > 0 && moves[0];
      if (move) {
        startIndex = move.oldIndex;
        endIndex = move.newIndex;
      }
    } else if (inserts) {
      const insert = inserts.length > 0 && inserts[0];
      startIndex = totalTracks - 1;
      endIndex = insert.newIndex;
    }
    // get all normalized offsets within range
    const invalidOffsets = D.Utils.RandomAccess.getOffsetsInRange(
      totalTracks,
      PLAYLIST_PAGE_SIZE,
      startIndex,
      endIndex,
    );
    return invalidateOffsets(state, pandoraId, invalidOffsets);
  }
  return state;
}

function onAppendItemsSuccess(state, action) {
  const { pandoraId, totalTracks } = action.response;
  const numAppendedTracks = action.params.content.request.itemPandoraIds.length;
  if (pandoraId && totalTracks >= 0 && numAppendedTracks >= 0) {
    // get all normalized offsets within range
    const invalidOffsets = D.Utils.RandomAccess.getOffsetsInRange(
      totalTracks,
      PLAYLIST_PAGE_SIZE,
      totalTracks - numAppendedTracks - 1,
      totalTracks - 1,
    );
    return invalidateOffsets(state, pandoraId, invalidOffsets);
  }
  return state;
}

function onDeleteTrack(state, action) {
  const { playlistId, itemId, trackIndex } = action.data;
  if (playlistId && itemId >= 0 && trackIndex >= 0) {
    // Keep track of the itemId and trackIndex of the track being deleted
    // once the track has been successfully deleted, the trackIndex is used to invalidate the corresponding offsets
    const tracksBeingDeleted = state.getIn([playlistId, ReducerKeys.TRACKS_BEING_DELETED]) || Map();
    return state.setIn(
      [playlistId, ReducerKeys.TRACKS_BEING_DELETED],
      tracksBeingDeleted.set(itemId, trackIndex),
    );
  }
  return state;
}

function onDeleteTracksSuccess(state, action) {
  const { pandoraId, totalTracks } = action.response;
  const trackItemIds = action.params.content.request.trackItemIds;
  const itemId = trackItemIds.length >= 0 && trackItemIds[0];
  if (pandoraId && totalTracks >= 0 && itemId >= 0) {
    const tracksBeingDeleted = state.getIn([pandoraId, ReducerKeys.TRACKS_BEING_DELETED]) || Map();
    const trackIndex = tracksBeingDeleted.get(itemId);
    const invalidOffsets = D.Utils.RandomAccess.getOffsetsInRange(
      totalTracks,
      PLAYLIST_PAGE_SIZE,
      totalTracks - 1,
      trackIndex,
    );
    return invalidateOffsets(state, pandoraId, invalidOffsets).setIn(
      [pandoraId, ReducerKeys.TRACKS_BEING_DELETED],
      tracksBeingDeleted.delete(itemId),
    );
  }
  return state;
}

function onDeleteTracksFail(state, action) {
  try {
    const data = JSON.parse(action.error.config.data);
    const pandoraId = data.request.pandoraId;
    const trackItemIds = data.request.trackItemIds;
    const itemId = trackItemIds.length >= 0 && trackItemIds[0];
    if (pandoraId && itemId >= 0) {
      const tracksBeingDeleted =
        state.getIn([pandoraId, ReducerKeys.TRACKS_BEING_DELETED]) || Map();
      return state.setIn(
        [pandoraId, ReducerKeys.TRACKS_BEING_DELETED],
        tracksBeingDeleted.delete(itemId),
      );
    }
  } catch (err) {
    return state.map(playlist => playlist.set(ReducerKeys.TRACKS_BEING_DELETED, Map()));
  }
  return state;
}
